import React, { useMemo, useRef, useCallback, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import {
  format,
  startOfWeek,
  addDays,
  isSameDay,
  isToday,
  subWeeks,
  addWeeks,
  differenceInDays,
} from 'date-fns';
import { DayTheme, DAY_THEME_ICONS } from '@/types';
import styles from './WeekStrip.module.css';

interface WeekStripProps {
  selectedDate: Date;
  onDateSelect: (date: Date) => void;
  dayThemes?: Record<string, DayTheme>; // date key (YYYY-MM-DD) -> theme
  ritualCompletedDates?: Set<string>; // date keys (YYYY-MM-DD) with completed shutdown ritual
}

const WEEKS_TO_SHOW = 12; // 12 weeks before and after current week
const DAY_CHIP_WIDTH = 58; // Approximate width of each day chip including gap
const WEEK_DIVIDER_WIDTH = 10; // Width of week divider

export function WeekStrip({
  selectedDate,
  onDateSelect,
  dayThemes,
  ritualCompletedDates,
}: WeekStripProps) {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isDraggingRef = useRef(false);
  const startXRef = useRef(0);
  const scrollLeftRef = useRef(0);
  const isInitialMount = useRef(true);

  // Mouse drag-to-scroll handlers
  const handleMouseDown = useCallback((e: React.MouseEvent) => {
    if (!scrollContainerRef.current) return;
    isDraggingRef.current = true;
    startXRef.current = e.pageX - scrollContainerRef.current.offsetLeft;
    scrollLeftRef.current = scrollContainerRef.current.scrollLeft;
    scrollContainerRef.current.style.cursor = 'grabbing';
  }, []);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!isDraggingRef.current || !scrollContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - scrollContainerRef.current.offsetLeft;
    const walk = (x - startXRef.current) * 1.5; // Scroll speed multiplier
    scrollContainerRef.current.scrollLeft = scrollLeftRef.current - walk;
  }, []);

  const handleMouseEnd = useCallback(() => {
    isDraggingRef.current = false;
    if (scrollContainerRef.current) {
      scrollContainerRef.current.style.cursor = 'grab';
    }
  }, []);

  // Generate multiple weeks of days centered around today
  const { weekDays, startDate } = useMemo(() => {
    const today = new Date();
    const startOfCurrentWeek = startOfWeek(today, { weekStartsOn: 1 });
    const start = subWeeks(startOfCurrentWeek, WEEKS_TO_SHOW);
    const totalDays = (WEEKS_TO_SHOW * 2 + 1) * 7; // 25 weeks total

    return {
      weekDays: Array.from({ length: totalDays }, (_, i) => addDays(start, i)),
      startDate: start,
    };
  }, []);

  // Scroll to selected date when it changes
  useLayoutEffect(() => {
    if (!scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const dayIndex = differenceInDays(selectedDate, startDate);

    // Calculate number of week dividers before this index
    const weekDividersCount = Math.floor(dayIndex / 7);

    // Calculate scroll position to center the selected day
    const dayPosition = dayIndex * DAY_CHIP_WIDTH + weekDividersCount * WEEK_DIVIDER_WIDTH;
    const containerWidth = container.clientWidth;
    const scrollLeft = dayPosition - containerWidth / 2 + DAY_CHIP_WIDTH / 2;

    container.scrollTo({
      left: Math.max(0, scrollLeft),
      behavior: isInitialMount.current ? 'instant' : 'smooth',
    });

    isInitialMount.current = false;
  }, [selectedDate, startDate]);

  // Navigate by week
  const navigateWeek = useCallback(
    (direction: 'prev' | 'next') => {
      const currentWeekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const newDate =
        direction === 'prev' ? subWeeks(currentWeekStart, 1) : addWeeks(currentWeekStart, 1);
      onDateSelect(newDate);
    },
    [selectedDate, onDateSelect]
  );

  // Get theme for a specific date
  const getThemeForDate = (day: Date): DayTheme => {
    if (!dayThemes) return null;
    const dateKey = format(day, 'yyyy-MM-dd');
    return dayThemes[dateKey] || null;
  };

  return (
    <nav className={styles.container} aria-label="Week navigation">
      <button
        className={styles.navButton}
        onClick={() => navigateWeek('prev')}
        aria-label="Previous week"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M10 12L6 8L10 4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div
        className={styles.scrollContainer}
        ref={scrollContainerRef}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseEnd}
        onMouseLeave={handleMouseEnd}
      >
        <div className={styles.weekStrip}>
          {weekDays.map((day, index) => {
            const isSelected = isSameDay(day, selectedDate);
            const dayIsToday = isToday(day);
            const theme = getThemeForDate(day);
            const hasTheme = theme !== null;
            const dateKey = format(day, 'yyyy-MM-dd');
            const ritualDone = ritualCompletedDates?.has(dateKey) ?? false;
            const isWeekStart = index % 7 === 0;

            return (
              <React.Fragment key={day.toISOString()}>
                {isWeekStart && index > 0 && (
                  <div className={styles.weekDivider} aria-hidden="true" />
                )}
                <motion.button
                  className={`${styles.dayChip} ${isSelected ? styles.active : ''} ${dayIsToday ? styles.today : ''} ${hasTheme ? styles.themed : ''} ${isSelected && hasTheme ? styles.themedActive : ''}`}
                  onClick={() => onDateSelect(day)}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  aria-pressed={isSelected}
                  aria-label={`${format(day, 'EEEE, MMMM d')}${dayIsToday ? ' (today)' : ''}${theme ? `, ${theme} theme` : ''}`}
                >
                  <span className={styles.dayName}>{format(day, 'EEE')}</span>
                  <span className={styles.dayNum}>{format(day, 'd')}</span>
                  {theme && (
                    <span
                      className={`${styles.themeEmoji} ${isSelected ? styles.themeEmojiActive : ''}`}
                      aria-hidden="true"
                    >
                      {DAY_THEME_ICONS[theme]}
                    </span>
                  )}
                  {ritualDone && (
                    <span className={styles.ritualCheck} aria-label="Shutdown ritual completed">
                      ✓
                    </span>
                  )}
                  {isSelected && (
                    <motion.div
                      className={`${styles.selectedIndicator} ${hasTheme ? styles.selectedIndicatorThemed : ''}`}
                      layoutId="selectedDay"
                      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
                    />
                  )}
                </motion.button>
              </React.Fragment>
            );
          })}
        </div>
      </div>

      <button
        className={styles.navButton}
        onClick={() => navigateWeek('next')}
        aria-label="Next week"
      >
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <path
            d="M6 12L10 8L6 4"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>
    </nav>
  );
}
